// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import { getUser } from "../auth/auth";
import { getMyMatches } from "../api/matches";
import iconFeed from "../assets/icon-feed.png";
import iconTradeExchange from "../assets/icon-trade-exchange.png";
import "../styles/TradeHub.css";

function TradeHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const userId = user?.id ?? null;
  const [matches, setMatches] = useState({ asTrader: [], asBountyOwner: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const searchParams = new URLSearchParams(location.search);
  const filteredPostType = searchParams.get("postType");
  const filteredPostId = searchParams.get("postId");
  const hasFilteredPost = Boolean(filteredPostType && filteredPostId);

  useEffect(() => {
    if (userId) {
      fetchMatches();
    } else {
      setMatches({ asTrader: [], asBountyOwner: [] });
      setLoading(false);
    }
  }, [userId]);

  const fetchMatches = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getMyMatches();
      setMatches({
        asTrader: data?.asTrader || [],
        asBountyOwner: data?.asBountyOwner || [],
      });
    } catch (error) {
      console.error("Error fetching matches:", error);
      setError(error?.message || "Could not load your matches.");
    } finally {
      setLoading(false);
    }
  };

  const traderMatches = (matches.asTrader || []).filter((match) => {
    if (!hasFilteredPost) return true;
    if (filteredPostType === "trade") {
      return String(match?.trade?.id || "") === String(filteredPostId);
    }
    if (filteredPostType === "bounty") {
      return String(match?.bounty?.id || "") === String(filteredPostId);
    }
    return true;
  });

  const bountyOwnerMatches = (matches.asBountyOwner || []).filter((match) => {
    if (!hasFilteredPost) return true;
    if (filteredPostType === "trade") {
      return String(match?.trade?.id || "") === String(filteredPostId);
    }
    if (filteredPostType === "bounty") {
      return String(match?.bounty?.id || "") === String(filteredPostId);
    }
    return true;
  });

  const filterLabel =
    filteredPostType === "trade"
      ? "Showing verified matches tied to one of your trade posts."
      : filteredPostType === "bounty"
        ? "Showing verified matches tied to one of your bounty posts."
        : "";

  const clearFilter = () => navigate("/trades");
  const showTraderSection =
    !hasFilteredPost || traderMatches.length > 0 || (traderMatches.length === 0 && bountyOwnerMatches.length === 0);
  const showBountyOwnerSection =
    !hasFilteredPost ||
    bountyOwnerMatches.length > 0 ||
    (traderMatches.length === 0 && bountyOwnerMatches.length === 0);

  const renderMatchCard = (match, role) => {
    const trade = match?.trade;
    const bounty = match?.bounty;
    const isTrader = role === "trader";
    const title = isTrader ? bounty?.cardName : trade?.cardName;
    const subtitle = isTrader
      ? `${bounty?.game || "Unknown game"} · Wants ${bounty?.desiredCondition || "any condition"}`
      : `${trade?.game || "Unknown game"} · Offering ${trade?.condition || "any condition"}`;
    const supporting = isTrader
      ? `Your trade: ${trade?.cardName || "Unknown card"}`
      : `Your bounty: ${bounty?.cardName || "Unknown card"}`;
    const imageUrl = isTrader ? bounty?.photoUrl : trade?.photoUrl;
    const destination = isTrader ? `/bounty-offer/${bounty?.id}` : `/trade-offer/${trade?.id}`;
    const ctaLabel = isTrader ? "Open bounty offer" : "Open trade offer";

    return (
      <button
        key={match.id}
        type="button"
        className="match-card"
        onClick={() => navigate(destination)}
      >
        <div className="match-card-top">
          <span className={`match-badge ${isTrader ? "trader" : "bounty-owner"}`}>
            {isTrader ? "Match for your trade" : "Match for your bounty"}
          </span>
          <span className="match-distance">
            {match?.distanceMiles != null ? `${match.distanceMiles} mi apart` : "Distance pending"}
          </span>
        </div>
        <div className="match-card-main">
          <div className="match-card-copy">
            <h3>{title || "Matched card"}</h3>
            <p className="match-primary">{subtitle}</p>
            <p className="match-secondary">{supporting}</p>
            <p className="match-meta">
              ZIP {isTrader ? bounty?.zipCode || "N/A" : trade?.zipCode || "N/A"} · Radius{" "}
              {isTrader ? bounty?.radius || 25 : trade?.radius || 25} mi
            </p>
          </div>
          <div className="match-card-visual">
            {imageUrl ? <img src={imageUrl} alt={title || "Matched card"} /> : <div className="match-card-placeholder">TCG</div>}
          </div>
        </div>
        <div className="match-card-footer">
          <span className="match-card-timestamp">
            Matched{" "}
            {match?.createdAt
              ? new Date(match.createdAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })
              : "recently"}
          </span>
          <span className="btn-send-offer">{ctaLabel}</span>
        </div>
      </button>
    );
  };

  const renderSection = (title, subtitle, sectionMatches, role) => (
    <section className="matches-panel">
      <div className="matches-panel-header">
        <div>
          <h2 className="matches-title">{title}</h2>
          <p className="matches-subtitle">{subtitle}</p>
        </div>
        <span className="matches-count">{sectionMatches.length}</span>
      </div>
      {sectionMatches.length === 0 ? (
        <div className="matches-empty">
          {loading
            ? "Checking nearby posts..."
            : "No active radius matches here yet. We’ll surface them automatically when a nearby post lines up."}
        </div>
      ) : (
        <div className="matches-list">{sectionMatches.map((match) => renderMatchCard(match, role))}</div>
      )}
    </section>
  );

  return (
    <>
      <TopNav />
      <div className="trade-hub-page page-with-top-nav">
        <div className="trade-hub-container">
          <div className="hub-heading">
            <div>
              <h1 className="hub-title">Trade Hub</h1>
              <p className="hub-subtitle">
                Post a card, post a bounty, and we’ll connect the two automatically when both sides fall within each
                other’s radius.
              </p>
            </div>
            {filteredPostType && filteredPostId && (
              <div className="hub-filter-pill">
                <span>{filterLabel}</span>
                <button type="button" onClick={clearFilter}>
                  Show all
                </button>
              </div>
            )}
          </div>

          <div className="hub-cards">
            <div className="hub-card hub-card-left" onClick={() => navigate("/post-bounty")}>
              <div className="hub-card-icon">
                <img src={iconFeed} alt="Post a Bounty" />
              </div>
              <h2>Post a Bounty</h2>
              <p>Looking for a specific card</p>
              <div className="hub-card-glow"></div>
            </div>

            <div className="hub-card hub-card-right" onClick={() => navigate("/post-trade")}>
              <div className="hub-card-icon">
                <img src={iconTradeExchange} alt="Post a Trade" />
              </div>
              <h2>Post a Trade</h2>
              <p>Offering a card for another</p>
              <div className="hub-card-glow"></div>
            </div>
          </div>

          {error && <div className="matches-error">{error}</div>}

          <div className="matches-grid">
            {showTraderSection &&
              renderSection(
                "Matches For Your Trades",
                "These are nearby bounty posts that match cards you already listed for trade.",
                traderMatches,
                "trader",
              )}
            {showBountyOwnerSection &&
              renderSection(
                "Matches For Your Bounties",
                "These are nearby trade posts that can fulfill bounties you posted.",
                bountyOwnerMatches,
                "bountyOwner",
              )}
          </div>
        </div>
      </div>
    </>
  );
}

export default TradeHub;
